import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Form,
  FormFeedback,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  CopyCutSubjectRequest,
  LocalizedProperty,
  State,
} from "../../interfaces";
import { ActivityButton } from "../../components/Common";
import { CourseSelect, GradeSelect } from "../../components";
import { copyCutSubject, resetCopyCutSubject } from "../../store/actions";

type TProps = {
  id: string;
  show: boolean;
  onCloseClick: (event: any) => void;
  action: string;
  name: LocalizedProperty | null;
};
export const CopyCutSubjectModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  action,
  name,
}) => {
  const history = useHistory();

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const {
    loading: copyCuttingSubject,
    error: copyCuttingError,
    success: copyCuttingSuccess,
  } = useSelector((state: any): State<any> => state.subject.copyCutSubject);

  //#region form
  const form = useFormik<CopyCutSubjectRequest>({
    enableReinitialize: true,
    initialValues: {
      action: action,
      subjectId: id,
      copyingToCourseId: "",
      copyingToGradeId: "",
    },
    initialErrors: copyCuttingError,
    validationSchema: Yup.object({
      subjectId: Yup.string().required(),
      copyingToCourseId: Yup.string().required(),
      copyingToGradeId: Yup.string().required(),
    }),
    onSubmit: (values) => {
      dispatch(copyCutSubject(values, history));
    },
  });
  //#endregion

  //#region effects
  //#region effects
  useEffect(() => {
    if (!copyCuttingSubject && copyCuttingSuccess) {
      if (action.toLowerCase() === "copy") {
        toast.success(t("subjectCopyingCompleted"), {
          autoClose: 3000,
        });
      } else if (action.toLowerCase() === "cut") {
        toast.success(t("subjectCutCompleted"), {
          autoClose: 3000,
        });
      }
      onClose();
    }
  }, [copyCuttingSubject, copyCuttingSuccess]);

  //#endregion
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
    dispatch(resetCopyCutSubject());
  };

  const getTitle = () => {
    if (action && name && name.ar && name.en) {
      return `${t(action)} ${i18n.language === "ar" ? name.ar : name.en}`;
    } else return `${t(action)}`;
  };

  //#endregion

  return (
    <Modal
      id="copyCutSubject"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {getTitle()}
        </ModalHeader>
        {typeof copyCuttingError === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{copyCuttingError}</p>
          </Alert>
        )}
        <ModalBody>
          <div className="mb-3">
            <Label htmlFor="copyingToCourseId" className="form-label">
              {t("copyToCourse")}
              <span className="text-danger">*</span>
            </Label>
            <CourseSelect
              type="select"
              id="copyingToCourseId"
              name="copyingToCourseId"
              className="form-select"
              aria-label={t("selectCourse")}
              placeholder={t("selectCourse")}
              value={form.values.copyingToCourseId}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.copyingToCourseId && form.errors.copyingToCourseId
                  ? true
                  : false
              }
            ></CourseSelect>
            {form.touched.copyingToCourseId && form.errors.copyingToCourseId ? (
              <FormFeedback type="invalid">
                {form.errors.copyingToCourseId}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label htmlFor="copyingToGradeId" className="form-label">
              {t("copyToGrade")}
              <span className="text-danger">*</span>
            </Label>
            <GradeSelect
              courseId={form.values.copyingToCourseId}
              type="select"
              id="copyingToGradeId"
              name="copyingToGradeId"
              className="form-select"
              aria-label={t("selectGrade")}
              placeholder={t("selectGrade")}
              value={form.values.copyingToGradeId}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.copyingToGradeId && form.errors.copyingToGradeId
                  ? true
                  : false
              }
            ></GradeSelect>
            {form.touched.copyingToGradeId && form.errors.copyingToGradeId ? (
              <FormFeedback type="invalid">
                {form.errors.copyingToGradeId}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title={t(action)}
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={copyCuttingSubject}
            disabled={copyCuttingSubject}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
